import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import Logo from '@/components/icons/Logo'
import { useAuth } from '@/contexts/AuthContext'
import { filterDemoSkills } from '@/lib/demo-skills'
import type { Skill } from '@/types/api'

interface NavigationDrawerProps {
  isOpen: boolean
  onClose: () => void
  skills?: Skill[]
}

export function NavigationDrawer({ isOpen, onClose, skills = [] }: NavigationDrawerProps) {
  const { logout } = useAuth()

  const handleLogout = async () => {
    onClose()
    await logout()
  }

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[150]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-80">
                  <div className="chrome-panel flex h-full flex-col border border-border-default/40">
                    <div className="flex h-20 items-center justify-between border-b border-border-default/30 px-6 text-text-primary">
                      <Logo height={80} color="currentColor" />
                      <button
                        type="button"
                        className="rounded-md text-text-tertiary hover:text-text-primary focus:outline-none"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      <nav className="flex flex-col space-y-1 p-4">
                        <Link
                          to="/skills"
                          onClick={onClose}
                          className="text-label-default flex items-center rounded-md bg-text-primary px-4 py-3 text-text-inverse transition-opacity hover:opacity-90"
                        >
                          Home
                        </Link>

                        {filterDemoSkills(skills).map((skill) => (
                          <Link
                            key={skill.id}
                            to={`/skills/${skill.id}`}
                            onClick={onClose}
                            className="text-label-default flex items-center rounded-md border border-border-default px-4 py-3 text-text-primary transition-colors hover:bg-surface-tertiary"
                          >
                            {capitalizeFirstLetter(skill.scope_name)} {capitalizeFirstLetter(skill.class_name)}
                          </Link>
                        ))}
                      </nav>
                    </div>

                    <div className="flex flex-col gap-2 border-t border-border-default/30 p-4">
                      <Link
                        to="/settings"
                        onClick={onClose}
                        className="text-label-default flex items-center justify-center rounded-md border border-border-default px-4 py-3 text-text-primary transition-colors hover:bg-surface-tertiary"
                      >
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          void handleLogout()
                        }}
                        className="text-label-default flex w-full items-center justify-center rounded-md bg-text-primary px-4 py-3 text-text-inverse transition-opacity hover:opacity-90"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
